import React, { useEffect, useRef } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const Section = styled.section`
  padding: 8rem 2rem;
  background: #000000ff;
  overflow: hidden;
  position: relative;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
`;

const Title = styled(motion.h2)`
  font-size: 3.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #ffffffff, #000000ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1rem;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.4rem;
  color: #aaa;
  margin-bottom: 5rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2.5rem;
`;

const FeatureCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(0, 122, 255, 0.2);
  border-radius: 24px;
  padding: 2rem;
  backdrop-filter: blur(12px);
  position: relative;
  overflow: hidden;
  transition: all 0.4s ease;

  &:hover {
    border-color: #007AFF;
    transform: translateY(-12px);
    box-shadow: 0 20px 40px rgba(0, 122, 255, 0.15);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 3px;
    background: linear-gradient(90deg, #007AFF, #00f5ff);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.5s ease;
  }

  &:hover::before {
    transform: scaleX(1);
  }
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(135deg, #007AFF, #00f5ff);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 25px rgba(0, 122, 255, 0.3);
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 0 0 1rem;
`;

const FeatureDesc = styled.p`
  color: #ccc;
  line-height: 1.7;
  font-size: 1.05rem;
`;

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0 }
};

const Features = () => {
  const { t } = useTranslation();
  const controls = useAnimation();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (inView) controls.start("visible");
  }, [inView, controls]);

  const features = [
    { icon: "target", title: t("feature_1_title"), desc: t("feature_1_desc") },
    { icon: "brain", title: t("feature_2_title"), desc: t("feature_2_desc") },
    { icon: "link", title: t("feature_3_title"), desc: t("feature_3_desc") },
    { icon: "calendar", title: t("feature_4_title"), desc: t("feature_4_desc") },
    { icon: "sync", title: t("feature_5_title"), desc: t("feature_5_desc") },
    { icon: "rocket", title: t("feature_6_title"), desc: t("feature_6_desc") }
  ];

  const getIcon = (type) => {
    const icons = {
      target: (
        <svg viewBox="0 0 24 24" width="36" height="36" fill="white">
          <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" fill="none"/>
          <circle cx="12" cy="12" r="6" stroke="white" strokeWidth="2" fill="none"/>
          <circle cx="12" cy="12" r="2" fill="white"/>
        </svg>
      ),
      brain: (
        <svg viewBox="0 0 24 24" width="36" height="36" fill="white">
          <path d="M12 4c-4.42 0-8 3.58-8 8 0 1.85.63 3.54 1.69 4.89C5.6 17.71 5 18.79 5 20c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2 0-1.21-.6-2.29-1.31-3.11C18.37 15.54 19 13.85 19 12c0-4.42-3.58-8-8-8zm0 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm1-6h-2V8h2v4z"/>
        </svg>
      ),
      link: (
        <svg viewBox="0 0 24 24" width="36" height="36" fill="white">
          <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1 0 1.71-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
        </svg>
      ),
      calendar: (
        <svg viewBox="0 0 24 24" width="36" height="36" fill="white">
          <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z"/>
        </svg>
      ),
      sync: (
        <svg viewBox="0 0 24 24" width="36" height="36" fill="white">
          <path d="M12 6v3l4-4-4-4v3c-4.42 0-8 3.58-8 8 0 1.57.46 3.03 1.24 4.26L6.7 14.8c-.45-.83-.7-1.79-.7-2.8 0-3.31 2.69-6 6-6zm6.76 1.74L17.3 9.2c.44.83.7 1.79.7 2.8 0 3.31-2.69 6-6 6v-3l-4 4 4 4v-3c4.42 0 8-3.58 8-8 0-1.57-.46-3.03-1.24-4.26z"/>
        </svg>
      ),
      rocket: (
        <svg viewBox="0 0 24 24" width="36" height="36" fill="white">
          <path d="M12 2.5c-3.15 0-5.83 1.97-6.9 4.73-.29.74-.07 1.56.5 2.05l.96.77c.49.39 1.21.33 1.65-.15.84-.92 2.1-1.4 3.29-1.4 1.19 0 2.45.48 3.29 1.4.44.48 1.16.54 1.65.15l.96-.77c.57-.49.79-1.31.5-2.05C17. 4.47 15.15 2.5 12 2.5zm-2.5 7c-.83 0-1.5-.67-1.5-1.5S8.67 6.5 9.5 6.5 11 7.17 11 8s-.67 1.5-1.5 1.5zm5 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5.88 19.12c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0l2.59-2.59c.39-.39.39-1.02 0-1.41-.39-.39-1.02-.39-1.41 0l-2.59 2.59z"/>
        </svg>
      )
    };
    return icons[type] || null;
  };

  return (
    <Section>
      <Container>
        <Title
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {t('features_title')}
        </Title>
        <Subtitle
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          {t('features_subtitle')}
        </Subtitle>

        <Grid ref={ref}>
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              variants={cardVariants}
              initial="hidden"
              animate={controls}
              transition={{ delay: index * 0.15, duration: 0.6 }}
            >
              <IconWrapper>{getIcon(feature.icon)}</IconWrapper>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDesc>{feature.desc}</FeatureDesc>
            </FeatureCard>
          ))}
        </Grid>
      </Container>
    </Section>
  );
};

export default Features;